// src/components/pages/watch/VideoPlayerPage.js
import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { db } from "../../../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  GlobalStyles,
  Portal,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import { useSeoRoute } from "../../../seo";

const VAST_TAG_URL =
  "https://pubads.g.doubleclick.net/gampad/adx?iu=/5021179/inssa&sz=2x2&c=635574567&cust_params=campaign%3Dinssa-concessionaire&m=text/xml";

// absurdly high z-index to guarantee we beat any ad overlays
const Z_ALWAYS_TOP = 2147483000;

export default function VideoPlayerPage() {
  const navigate = useNavigate();
  const { episodeId } = useParams();
  const { state } = useLocation();
  const videoMetaFromState = state?.videoMeta || null;
  const autoPlay = state?.autoPlay ?? true;
  const [videoMeta, setVideoMeta] = useState(videoMetaFromState);
  const [isVideoLoading, setIsVideoLoading] = useState(!videoMetaFromState);
  const [videoLoadError, setVideoLoadError] = useState("");
  const isSponsored = videoMeta?.sponsoredContent === true;

  const videoRef = useRef(null);
  const adContainerRef = useRef(null);
  const adsInitializedRef = useRef(false);
  const adRequestedRef = useRef(false);
  const hasResumedRef = useRef(false);
  const autoPlayedRef = useRef(false);
  const exitTriggeredRef = useRef(false); // prevent multiple navigations

  const [showOverlay, setShowOverlay] = useState(false);
  const [imaReady, setImaReady] = useState(false);
  const [adPlaying, setAdPlaying] = useState(false);
  const [needsUserGesture, setNeedsUserGesture] = useState(false); // show a tap-to-play overlay if autoplay with sound is blocked

  useSeoRoute(
    videoMeta
      ? {
          id: videoMeta.id || episodeId,
          title: videoMeta.title || videoMeta.name,
          description: videoMeta.description || "",
          image: videoMeta.posterUrl || videoMeta.thumbnailUrl || "",
          publishedAt: videoMeta.publishedAt || videoMeta.createdAt || "",
          duration: videoMeta.duration || "",
        }
      : { id: episodeId }
  );

  useEffect(() => {
    let isCancelled = false;

    const loadVideoMetaFromRoute = async () => {
      if (videoMetaFromState) {
        setVideoMeta(videoMetaFromState);
        setVideoLoadError("");
        setIsVideoLoading(false);
        return;
      }

      if (!episodeId) {
        setVideoMeta(null);
        setVideoLoadError("Missing video ID.");
        setIsVideoLoading(false);
        return;
      }

      setIsVideoLoading(true);
      setVideoLoadError("");

      try {
        // 1) Movie by id
        try {
          const movieSnap = await getDoc(doc(db, "movies", episodeId));
          if (!isCancelled && movieSnap.exists()) {
            setVideoMeta({ id: movieSnap.id, ...movieSnap.data() });
            return;
          }
        } catch (err) {
          console.warn("Movie lookup failed:", err);
        }

        // 2) Top-level episode by id
        try {
          const topLevelEpisodeSnap = await getDoc(doc(db, "episodes", episodeId));
          if (!isCancelled && topLevelEpisodeSnap.exists()) {
            setVideoMeta({ id: topLevelEpisodeSnap.id, ...topLevelEpisodeSnap.data() });
            return;
          }
        } catch (err) {
          console.warn("Top-level episode lookup failed:", err);
        }

        // 3) Final fallback: traverse series -> seasons -> episodes/{episodeId}
        try {
          const seriesSnap = await getDocs(collection(db, "series"));
          for (const seriesDoc of seriesSnap.docs) {
            const seasonsSnap = await getDocs(
              collection(db, "series", seriesDoc.id, "seasons")
            );
            for (const seasonDoc of seasonsSnap.docs) {
              const nestedEpisodeSnap = await getDoc(
                doc(
                  db,
                  "series",
                  seriesDoc.id,
                  "seasons",
                  seasonDoc.id,
                  "episodes",
                  episodeId
                )
              );

              if (!isCancelled && nestedEpisodeSnap.exists()) {
                setVideoMeta({ id: nestedEpisodeSnap.id, ...nestedEpisodeSnap.data() });
                return;
              }
            }
          }
        } catch (err) {
          console.warn("Hierarchical episode lookup failed:", err);
        }

        if (!isCancelled) {
          setVideoMeta(null);
          setVideoLoadError("Video not found.");
        }
      } catch (err) {
        console.error("Error fetching video metadata:", err);
        if (!isCancelled) {
          setVideoMeta(null);
          setVideoLoadError("Unable to load this video right now.");
        }
      } finally {
        if (!isCancelled) setIsVideoLoading(false);
      }
    };

    loadVideoMetaFromRoute();

    return () => {
      isCancelled = true;
    };
  }, [episodeId, videoMetaFromState]);

  // Hide the SDK skip UI if you want only VAST-driven skip styling
  const skipUiHideStyles = {
    ".ima-skip-button": { display: "none !important" }
  };

  // Page-level global adjustments: black bg, remove scroll bounce
  const viewportGlobalStyles = (theme) => ({
    html: { backgroundColor: theme.palette.common.black },
    body: {
      backgroundColor: theme.palette.common.black,
      overscrollBehavior: "contain"
    },
    "#root": { backgroundColor: theme.palette.common.black }
  });

  // Ensure iOS honors safe-area insets (viewport-fit=cover)
  useEffect(() => {
    const existing = document.querySelector('meta[name="viewport"]');
    const originalContent = existing?.getAttribute("content") || "";
    const withCover = originalContent.includes("viewport-fit=cover")
      ? originalContent
      : (originalContent ? originalContent + ", " : "") + "viewport-fit=cover";

    // Apply (or insert) the meta tag while this page is active
    let created = false;
    if (existing) {
      existing.setAttribute("content", withCover);
    } else {
      const meta = document.createElement("meta");
      meta.setAttribute("name", "viewport");
      meta.setAttribute("content", "width=device-width, initial-scale=1, viewport-fit=cover");
      document.head.appendChild(meta);
      created = true;
    }

    return () => {
      // restore on unmount
      if (created) {
        const el = document.querySelector('meta[name="viewport"]');
        if (el) el.remove();
      } else if (existing) {
        existing.setAttribute("content", originalContent);
      }
    };
  }, []);

  // Lock body scrolling while this page is mounted
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Ensure inline playback on mobile (without forcing mute)
  useEffect(() => {
    const videoEl = videoRef.current;
    if (videoEl) {
      videoEl.setAttribute("playsinline", "");
      videoEl.setAttribute("webkit-playsinline", "");
      // Explicitly ensure sound is enabled by default
      videoEl.muted = false;
      videoEl.volume = 1;
    }
  }, []);

  // 1) Load IMA SDK (only if not sponsored)
  useEffect(() => {
    if (isSponsored) return;
    if (window.google?.ima) {
      setImaReady(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://imasdk.googleapis.com/js/sdkloader/ima3.js";
    script.async = true;
    script.onload = () => setImaReady(true);
    document.head.appendChild(script);
  }, [isSponsored]);

  // 2) Initialize IMA & intercept first play (only if not sponsored)
  useEffect(() => {
    if (isSponsored) return;
    if (
      !imaReady ||
      !videoMeta ||
      !videoRef.current ||
      !adContainerRef.current ||
      adsInitializedRef.current
    ) {
      return;
    }
    adsInitializedRef.current = true;

    const videoEl = videoRef.current;           // capture ref target for this effect
    const adUI = adContainerRef.current;
    const adDisplayContainer = new window.google.ima.AdDisplayContainer(
      adUI,
      videoEl
    );
    const adsLoader = new window.google.ima.AdsLoader(adDisplayContainer);

    adsLoader.addEventListener(
      window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      (e) => {
        const adsManager = e.getAdsManager(videoEl);

        // Ensure ad audio is ON
        try { adsManager.setVolume(1); } catch {}

        adsManager.addEventListener(
          window.google.ima.AdErrorEvent.Type.AD_ERROR,
          () => {
            adsManager.destroy();
            setAdPlaying(false);
            videoEl.src = videoMeta.fullVideoUrl;
            videoEl.muted = false;
            videoEl.volume = 1;
            videoEl.load();
            videoEl.play().catch(() => {});
          }
        );

        adsManager.addEventListener(
          window.google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
          () => setAdPlaying(true)
        );

        adsManager.addEventListener(
          window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
          () => {
            if (!hasResumedRef.current) {
              hasResumedRef.current = true;
              adsManager.destroy();
              setAdPlaying(false);
              videoEl.src = videoMeta.fullVideoUrl;
              videoEl.muted = false;
              videoEl.volume = 1;
              videoEl.load();
              videoEl.play().catch(() => {});
            }
          }
        );

        // Use current client rect (safe-area boxed)
        const rect = videoEl.getBoundingClientRect();
        adsManager.init(rect.width, rect.height, window.google.ima.ViewMode.NORMAL);

        try {
          adsManager.start();
          try { adsManager.setVolume(1); } catch {}
        } catch {
          adsManager.destroy();
          setAdPlaying(false);
          videoEl.muted = false;
          videoEl.volume = 1;
          videoEl.play().catch(() => {});
        }

        // Keep ad viewport sized correctly on rotation/resize
        const handleResize = () => {
          try {
            const r = videoEl.getBoundingClientRect();
            adsManager.resize(r.width, r.height, window.google.ima.ViewMode.NORMAL);
          } catch {}
        };
        window.addEventListener("resize", handleResize);
        window.addEventListener("orientationchange", handleResize);

        // Clean up listeners when we leave
        const cleanup = () => {
          window.removeEventListener("resize", handleResize);
          window.removeEventListener("orientationchange", handleResize);
        };
        // store on the captured element, not on videoRef.current
        videoEl._imaCleanup = cleanup;
      }
    );

    adsLoader.addEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      () => {
        setAdPlaying(false);
        // local read is fine here, but we can still prefer the captured el
        const el = videoEl;
        if (!el) return;
        el.src = videoMeta.fullVideoUrl;
        el.muted = false;
        el.volume = 1;
        el.load();
        el.play().catch(() => {});
      }
    );

    const onFirstPlay = () => {
      if (adRequestedRef.current) {
        return videoEl.play().catch(() => {});
      }
      adRequestedRef.current = true;

      // IMPORTANT: initialize AdDisplayContainer on user gesture so audio is allowed
      adDisplayContainer.initialize();

      const rect = videoEl.getBoundingClientRect();

      const adsRequest = new window.google.ima.AdsRequest();
      adsRequest.adTagUrl = VAST_TAG_URL;
      adsRequest.linearAdSlotWidth = rect.width;
      adsRequest.linearAdSlotHeight = rect.height;
      adsRequest.nonLinearAdSlotWidth = rect.width;
      adsRequest.nonLinearAdSlotHeight = Math.floor(rect.height / 3);

      adsLoader.requestAds(adsRequest);
      videoEl.pause(); // pause content until ads finish
    };

    videoEl.addEventListener("play", onFirstPlay, { once: true });

    // Cleanup uses the captured element, not videoRef.current
    return () => {
      videoEl.removeEventListener("play", onFirstPlay);
      if (videoEl?._imaCleanup) {
        try { videoEl._imaCleanup(); } catch {}
        delete videoEl._imaCleanup;
      }
    };
  }, [imaReady, videoMeta, isSponsored]);

  // 3) Attempt autoplay with sound; if blocked by the browser, show a Tap-to-Play overlay
  useEffect(() => {
    if (!autoPlay || autoPlayedRef.current || !videoRef.current) return;
    if (!isSponsored && !imaReady) return;

    autoPlayedRef.current = true;
    const v = videoRef.current;
    v.muted = false;
    v.volume = 1;

    v.play().catch(() => {
      setNeedsUserGesture(false);
      v.muted = false;
      v.volume = 1;
    });
  }, [autoPlay, imaReady, isSponsored]);

  // Guard against pauses during ad playback
  const handleVideoPause = () => {
    if (adPlaying && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  // single-exit helper
  const maybeExit = () => {
    if (exitTriggeredRef.current) return;
    if (adPlaying) return; // never exit while an ad is running
    exitTriggeredRef.current = true;
    navigate(-1);
  };

  const handleVideoEnd = () => {
    maybeExit();
  };

  // time-based fallback when 'ended' doesn't fire (HLS/MSE edge cases)
  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    const d = v.duration;
    if (!Number.isFinite(d) || d <= 0) return;

    // Exit when within 0.5s of the end (and not during ad)
    const remaining = d - v.currentTime;
    if (remaining <= 0.5) {
      maybeExit();
    }
  };

  const startFromUserGesture = () => {
    const v = videoRef.current;
    if (!v) return;
    setNeedsUserGesture(false);
    v.muted = false;
    v.volume = 1;
    v.play().catch(() => {
      setNeedsUserGesture(true);
    });
  };

  // Fallback if no videoMeta
  if (isVideoLoading) {
    return (
      <Box p={4}>
        <Typography>Loading video…</Typography>
      </Box>
    );
  }

  if (!videoMeta) {
    return (
      <Box p={4}>
        <Typography>{videoLoadError || "No video data. Please go back."}</Typography>
        <IconButton onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          <ArrowBackIcon />
        </IconButton>
      </Box>
    );
  }

  const {
    title,
    description,
    fullVideoUrl,
    artist,
    mpaRating,
    year,
    category = [],
    tags = [],
  } = videoMeta;

  const nativeControlsEnabled = !adPlaying;

  return (
    <>
      <GlobalStyles styles={skipUiHideStyles} />
      <GlobalStyles styles={viewportGlobalStyles} />

      {/* Always-on-top back button rendered in a Portal */}
      <Portal>
        <Box
          sx={{
            position: "fixed",
            top: "max(16px, env(safe-area-inset-top))",
            left: "max(16px, env(safe-area-inset-left))",
            zIndex: Z_ALWAYS_TOP,
            pointerEvents: "auto"
          }}
        >
          <IconButton
            aria-label="Go back"
            onClick={() => navigate(-1)}
            sx={{
              color: "common.white",
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.5),
              width: 44,
              height: 44,
              "&:hover": { bgcolor: (theme) => alpha(theme.palette.common.black, 0.7) }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
      </Portal>

      {/* Full-viewport black backing */}
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100dvh",
          minHeight: "100svh",
          backgroundColor: "common.black",
          overflow: "hidden"
        }}
      >
        {/* Safe-area bounded stage: keeps controls away from notch/home indicator */}
        <Box
          sx={{
            position: "absolute",
            top: "env(safe-area-inset-top)",
            bottom: "env(safe-area-inset-bottom)",
            left: "env(safe-area-inset-left)",
            right: "env(safe-area-inset-right)"
          }}
        >
          {/* Info button (hidden during ads) */}
          {!showOverlay && !adPlaying && (
            <IconButton
              onClick={() => setShowOverlay(true)}
              aria-label="Show info"
              sx={{
                position: "absolute",
                top: 16, // already inside safe area
                right: 40,
                zIndex: 10,
                color: "common.white",
                width: 40,
                height: 40
              }}
            >
              <InfoIcon />
            </IconButton>
          )}

          {/* Main content video */}
          <video
            ref={videoRef}
            src={fullVideoUrl}
            controls={nativeControlsEnabled}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              backgroundColor: "common.black",
              // prevent interacting with the content video while an ad is on top
              pointerEvents: adPlaying ? "none" : "auto"
            }}
            // discourage saving/casting where supported
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            disableRemotePlayback
            // block right-click save as
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
            onPause={handleVideoPause}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnd}
          />

          {/* IMA ad UI container (matches safe-area size) */}
          <Box
            ref={adContainerRef}
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: adPlaying ? "auto" : "none",
              zIndex: 5,
              cursor: adPlaying ? "pointer" : "auto"
            }}
          />

          {/* metadata overlay */}
          {showOverlay && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.8),
                color: "common.white",
                p: 3,
                maxHeight: "60%",
                overflowY: "auto",
                zIndex: 10
              }}
            >
              <IconButton
                onClick={() => setShowOverlay(false)}
                aria-label="Close info"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  color: "common.white"
                }}
              >
                <CloseIcon />
              </IconButton>

              <Typography variant="h5" gutterBottom>
                {title}
              </Typography>
              <Typography variant="body2" paragraph>
                {description}
              </Typography>
              {artist && (
                <Typography variant="body2">
                  <strong>Artist:</strong> {artist}
                </Typography>
              )}
              {mpaRating && (
                <Typography variant="body2">
                  <strong>MPA Rating:</strong> {mpaRating}
                </Typography>
              )}
              {year && (
                <Typography variant="body2" paragraph>
                  <strong>Year:</strong> {year}
                </Typography>
              )}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                {category.map((c) => (
                  <Chip key={c} label={c} size="small" />
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {tags.map((t) => (
                  <Chip key={t} label={t} size="small" />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Simple "tap to play" overlay when autoplay with sound is blocked */}
        {needsUserGesture && (
          <Box
            onClick={startFromUserGesture}
            role="button"
            aria-label="Tap to play"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "common.white",
              zIndex: Z_ALWAYS_TOP - 1,
              cursor: "pointer",
              background: (theme) =>
                `linear-gradient(to top, ${alpha(theme.palette.common.black, 0.65)}, ${alpha(theme.palette.common.black, 0.35)})`
            }}
          >
            <Typography variant="h6">Tap to play</Typography>
          </Box>
        )}
      </Box>
    </>
  );
}
